import { getFormErrors } from "@msuite/ui/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal } from "@msuite/ui/modal";
import { Tabs } from "@msuite/ui/tabs";
import { guardArray } from "@msuite/utils/array";
import type { OrderFormSchema } from "@msuite/utils/schemas";
import { useFormContext, useWatch } from "react-hook-form";
import { OrderFormContext } from "./context";
import { OrderFormArticles } from "./order-form-articles";
import { OrderFormDelivery } from "./order-form-delivery";
import { OrderFormLoadCarriers } from "./order-form-load-carriers";

/** Props Interface */
interface OrderFormProps {
	isProjectLocked?: boolean;
	projectScope?: "organization" | "ownProjects";
}

export function OrderForm({ isProjectLocked, projectScope }: OrderFormProps) {
	/** Hooks */
	const { t } = useTranslation();
	const methods = useFormContext<OrderFormSchema>();
	const articles = useWatch({ control: methods.control, name: "articles" });
	const loadCarriers = useWatch({ control: methods.control, name: "loadCarriers" });

	/** Functions */
	function getItemIndex(articleId: string) {
		return guardArray(articles).findIndex((article) => article.articleId === articleId);
	}

	function getLoadCarrierIndex(loadCarrierId: string) {
		return guardArray(loadCarriers).findIndex((line) => line.loadCarrierId === loadCarrierId);
	}

	/** Render */
	return (
		<OrderFormContext.Provider
			value={{ getItemIndex, getLoadCarrierIndex, isProjectLocked, projectScope }}
		>
			<Modal.TableWrapper>
				<Tabs
					panelKey="order-form-tabs"
					groups={[
						{
							name: "order-form",
							panels: [
								{
									label: t("stock.articles"),
									cyId: "articles-tab",
									count: ` ${guardArray(articles).length}`,
									panel: () => <OrderFormArticles />,
									errors: getFormErrors(methods.formState.errors, ["articles"]),
								},
								{
									label: t("stock.leergut"),
									cyId: "load-carriers-tab",
									count: ` ${guardArray(loadCarriers).filter((line) => line.quantity > 0).length}`,
									panel: () => <OrderFormLoadCarriers />,
									errors: getFormErrors(methods.formState.errors, ["loadCarriers"]),
								},
								{
									label: t("stock.delivery"),
									cyId: "delivery-tab",
									panel: () => <OrderFormDelivery />,
									errors: getFormErrors(methods.formState.errors, [
										"deliveryDate",
										"projectId",
										"tourWaveId",
									]),
								},
							],
						},
					]}
				/>
			</Modal.TableWrapper>
		</OrderFormContext.Provider>
	);
}
